import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api-client';
import { env } from '@/env';
import { isNative, tokenSync } from '@/lib/native-auth';
import {
  talhaoResumoListSchema,
  talhaoDetailSchema,
  relatorioListSchema,
  relatorioDetailSchema,
  soloSchema,
  climaSchema,
  produtorListSchema,
  fazendaResumoListSchema,
  manejoAplicacaoListSchema,
  datasVisitaListSchema,
  visitaRevisarListSchema,
  fazendaEditSchema,
  insumoListSchema,
  type FazendaResumo,
} from './schemas';
import { mipCatalogoSchema, visitaBundleSchema } from './visita/schemas';
import {
  visitasPendentes,
  putApiCache,
  getApiCache,
  getPropsSyncInfo,
  saveBundle,
  putTile,
  putFazMaxZoom,
} from './visita/offline';
import {
  tilesForGeojsons,
  GOOGLE_SAT_TILE,
  centerOf,
  registerSentinelMosaic,
  s2NdviTemplate,
  s2NdmiTemplate,
  tileFromTemplate,
} from './visita/staticmap';
import { syncVisita } from './visita/api';

// Leitura com cache offline: rede primeiro (e grava no Dexie); em falha de REDE
// serve o que foi sincronizado. Não confia só no navigator.onLine — no WebView
// Android ele fica true sem internet e o fetch pendura/falha sem cair no cache
// (tela travada em "Carregando…"). Timeout de 8s + fallback em qualquer erro
// que NÃO seja HTTP do servidor (ApiError 401/500 continua propagando online).
async function cachedApi<T>(path: string, schema: z.ZodType<T>, signal?: AbortSignal): Promise<T> {
  try {
    let sig = signal;
    if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
      const to = AbortSignal.timeout(8000);
      sig = signal && 'any' in AbortSignal ? AbortSignal.any([signal, to]) : (signal ?? to);
    }
    const d = await api(path, sig ? { schema, signal: sig } : { schema });
    void putApiCache(path, d);
    return d;
  } catch (e) {
    const falhaDeRede =
      !(e instanceof ApiError) ||   // AbortError (timeout) e afins
      e.status === 0 ||             // NETWORK_ERROR do api-client (fetch falhou)
      (typeof navigator !== 'undefined' && navigator.onLine === false);
    if (falhaDeRede) {
      const cached = await getApiCache(path);
      if (cached !== undefined) {
        const r = schema.safeParse(cached);
        if (r.success) return r.data;
      }
    }
    throw e;
  }
}

// Sincroniza propriedades pro uso offline: produtores → fazendas → talhões (+ dados
// de cada talhão) + bundle + mapas (satélite/NDVI/NDMI). Best-effort por fazenda.
// Com `cpf`, sincroniza SÓ as fazendas daquele produtor (não todas).
export async function sincronizarPropriedades(
  onProgress?: (done: number, total: number, label: string) => void,
  cpf?: string,
): Promise<{ produtores: number; fazendas: number }> {
  const produtores: { cpf: string }[] = cpf
    ? [{ cpf }]
    : await cachedApi('/api/v2/agronomia/produtores', produtorListSchema);
  const fazByCpf: Record<string, FazendaResumo[]> = {};
  let total = 0;
  for (const p of produtores) {
    const fz = await cachedApi(`/api/v2/agronomia/produtores/${p.cpf}/fazendas`, fazendaResumoListSchema);
    fazByCpf[p.cpf] = fz;
    total += fz.length;
  }
  let done = 0;
  for (const p of produtores) {
    for (const f of fazByCpf[p.cpf] ?? []) {
      onProgress?.(done, total, f.fazenda ?? 'Fazenda');
      try {
        // Talhões + TODOS os dados de cada talhão (detalhe + histórico de relatórios).
        const tlist = await cachedApi(`/api/v2/imoveis/${f.imovel_id}/talhoes`, talhaoResumoListSchema);
        for (const t of tlist) {
          try { await cachedApi(`/api/v2/talhoes/${t.talhao_id}`, talhaoDetailSchema); } catch { /* segue */ }
          try { await cachedApi(`/api/v2/talhoes/${t.talhao_id}/relatorios`, relatorioListSchema); } catch { /* segue */ }
        }
      } catch { /* segue */ }
      try {
        const b = await api(`/api/v2/imoveis/${f.imovel_id}/visita-bundle`, { schema: visitaBundleSchema });
        await saveBundle(f.imovel_id, b);
        // Tiles pro mapa interativo offline: satélite (Esri) + NDVI/NDMI (Sentinel).
        const geos = (b.talhoes ?? []).map((t) => t.geojson).filter(Boolean);
        const { tiles, maxZoom } = tilesForGeojsons(geos);
        await putFazMaxZoom(f.imovel_id, maxZoom);
        // Baixa em lotes (até ~1400 tiles/fazenda p/ zoom fundo) — não dispara tudo de
        // uma vez pra não afogar a rede do device nem tomar rate-limit do Google.
        const baixarTiles = async (list: { z: number; x: number; y: number }[], pref: string, mkUrl: (z: number, x: number, y: number) => string) => {
          const LOTE = 48;
          for (let i = 0; i < list.length; i += LOTE) {
            await Promise.all(list.slice(i, i + LOTE).map(async (tl) => {
              const id = `${pref}/${tl.z}/${tl.x}/${tl.y}`;
              try { const r = await fetch(mkUrl(tl.z, tl.x, tl.y)); if (r.ok) await putTile(id, await r.blob()); } catch { /* faltando */ }
            }));
          }
        };
        await baixarTiles(tiles, 'sat', GOOGLE_SAT_TILE);
        // NDVI/NDMI via Planetary Computer (API pública) — até z15 (Sentinel 10m).
        const center = centerOf(geos);
        if (center) {
          const sid = await registerSentinelMosaic(center.lon, center.lat);
          if (sid) {
            const idx = tiles.filter((t) => t.z <= 15);
            for (const [pref, tpl] of [['ndvi', s2NdviTemplate(sid)], ['ndmi', s2NdmiTemplate(sid)]] as const) {
              await baixarTiles(idx, pref, (z, x, y) => tileFromTemplate(tpl, z, x, y));
            }
          }
        }
      } catch { /* segue */ }
      done++;
      onProgress?.(done, total, f.fazenda ?? 'Fazenda');
    }
  }
  const infoKey = cpf ? `__props_sync_${cpf.replace(/\D/g, '')}__` : '__props_sync__';
  await putApiCache(infoKey, { ts: Date.now(), produtores: produtores.length, fazendas: total });
  return { produtores: produtores.length, fazendas: total };
}

export function useSincronizarPropriedades(cpf?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (onProgress?: (done: number, total: number, label: string) => void) =>
      sincronizarPropriedades(onProgress, cpf),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agronomia'] });
    },
  });
}

export function usePropsSyncInfo(cpf?: string) {
  return useQuery({
    queryKey: ['agronomia', 'props-sync-info', cpf ?? ''],
    queryFn: () => getPropsSyncInfo(cpf),
    staleTime: 2_000,
  });
}


const keys = {
  talhoes: () => ['agronomia', 'talhoes'] as const,
  talhao: (id: number | string) => ['agronomia', 'talhao', String(id)] as const,
  relatorios: (id: number | string) => ['agronomia', 'relatorios', String(id)] as const,
  relatorio: (id: number | string) => ['agronomia', 'relatorio', String(id)] as const,
  solo: (id: number | string) => ['agronomia', 'solo', String(id)] as const,
};

export function useTalhoes() {
  return useQuery({
    queryKey: keys.talhoes(),
    queryFn: ({ signal }) =>
      api('/api/v2/talhoes', { schema: talhaoResumoListSchema, signal }),
    staleTime: 30_000,
  });
}

// ── Hierarquia Produtor → Fazenda (fluxo novo) ───────────────────────────────
export function useProdutores() {
  return useQuery({
    queryKey: ['agronomia', 'produtores'],
    queryFn: ({ signal }) => cachedApi('/api/v2/agronomia/produtores', produtorListSchema, signal),
    staleTime: 30_000,
    networkMode: 'always', // offline serve o cache sincronizado
  });
}

export function useFazendasDoProdutor(cpf: string | null) {
  return useQuery({
    queryKey: ['agronomia', 'produtor', cpf ?? '', 'fazendas'],
    queryFn: ({ signal }) => cachedApi(`/api/v2/agronomia/produtores/${cpf}/fazendas`, fazendaResumoListSchema, signal),
    enabled: !!cpf,
    staleTime: 30_000,
    networkMode: 'always',
  });
}

export function useFazendaTalhoes(imovelId: number | null) {
  return useQuery({
    queryKey: ['agronomia', 'fazenda', String(imovelId ?? ''), 'talhoes'],
    queryFn: ({ signal }) => cachedApi(`/api/v2/imoveis/${imovelId}/talhoes`, talhaoResumoListSchema, signal),
    enabled: imovelId != null,
    staleTime: 30_000,
    networkMode: 'always',
  });
}

export function useTalhao(id: number | null) {
  return useQuery({
    queryKey: keys.talhao(id ?? ''),
    queryFn: ({ signal }) => cachedApi(`/api/v2/talhoes/${id}`, talhaoDetailSchema, signal),
    enabled: id != null,
    staleTime: 30_000,
    networkMode: 'always',
  });
}

export function useTalhaoRelatorios(id: number | null) {
  return useQuery({
    queryKey: keys.relatorios(id ?? ''),
    queryFn: ({ signal }) => cachedApi(`/api/v2/talhoes/${id}/relatorios`, relatorioListSchema, signal),
    enabled: id != null,
    staleTime: 30_000,
    networkMode: 'always',
  });
}

export function useRelatorio(id: number | null) {
  return useQuery({
    queryKey: keys.relatorio(id ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/relatorios/${id}`, { schema: relatorioDetailSchema, signal }),
    enabled: id != null,
    staleTime: 30_000,
  });
}

export function useTalhaoSolo(id: number | null) {
  return useQuery({
    queryKey: keys.solo(id ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/talhoes/${id}/solo`, { schema: soloSchema, signal }),
    enabled: id != null,
    staleTime: 5 * 60_000,
  });
}

export function useClima(imovelId: number | null) {
  return useQuery({
    queryKey: ['agronomia', 'clima', String(imovelId ?? '')],
    queryFn: ({ signal }) =>
      api(`/api/v2/imoveis/${imovelId}/clima`, { schema: climaSchema, signal }),
    enabled: imovelId != null,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

// ── Sincronização + revisão (fluxo: sincronizar → revisar → salvar → relatório) ─
export function useVisitasPendentes(imovelId: number | null) {
  return useQuery({
    queryKey: ['agronomia', 'pendentes', String(imovelId ?? '')],
    queryFn: () => visitasPendentes(imovelId != null ? [imovelId] : undefined),
    enabled: imovelId != null,
    staleTime: 5_000,
  });
}

export function useSincronizarFazenda(imovelId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const pend = await visitasPendentes(imovelId != null ? [imovelId] : undefined);
      for (const v of pend) await syncVisita(v.clientUuid);
      return pend.length;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agronomia', 'pendentes'] });
      void qc.invalidateQueries({ queryKey: ['agronomia', 'visitas'] });
    },
  });
}

export function useVisitasFazenda(imovelId: number | null) {
  return useQuery({
    queryKey: ['agronomia', 'visitas', String(imovelId ?? '')],
    queryFn: ({ signal }) =>
      api(`/api/v2/agronomia/imoveis/${imovelId}/visitas`, { schema: visitaRevisarListSchema, signal }),
    enabled: imovelId != null,
    staleTime: 20_000,
    retry: false,
  });
}

export function useExcluirVisita(imovelId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (visitaId: number) =>
      api(`/api/v2/agronomia/visitas/${visitaId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agronomia', 'visitas', String(imovelId ?? '')] });
      void qc.invalidateQueries({ queryKey: ['agronomia', 'datas-visita', String(imovelId ?? '')] });
    },
  });
}

// ── Datas com visita (calendário do relatório, tela 12) ──────────────────────
export function useDatasVisita(imovelId: number | null) {
  return useQuery({
    queryKey: ['agronomia', 'datas-visita', String(imovelId ?? '')],
    queryFn: ({ signal }) =>
      api(`/api/v2/agronomia/imoveis/${imovelId}/datas-visita`, { schema: datasVisitaListSchema, signal }),
    enabled: imovelId != null,
    staleTime: 60_000,
    retry: false,
  });
}

// Datas com visita em VÁRIAS fazendas (consolidado do produtor) — os pontinhos
// do calendário passam a marcar o dia que teve visita em qualquer uma delas.
export function useDatasVisitaMulti(imovelIds: number[]) {
  const ids = [...imovelIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: ['agronomia', 'datas-visita-multi', ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const total = new Map<string, number>();
      for (const id of ids) {
        try {
          const rows = await api(`/api/v2/agronomia/imoveis/${id}/datas-visita`, { schema: datasVisitaListSchema, signal });
          for (const d of rows) total.set(d.data, (total.get(d.data) ?? 0) + d.visitas);
        } catch { /* fazenda sem visita ou fora do alcance — segue */ }
      }
      return [...total.entries()]
        .map(([data, visitas]) => ({ data, visitas }))
        .sort((a, b) => a.data.localeCompare(b.data));
    },
  });
}

/** Consolidado de VÁRIAS fazendas (HTML) — link abrível. */
export function relatorioConsolidadoMultiUrl(imovelIds: number[], from?: string, to?: string): string {
  const sp = new URLSearchParams({ imoveis: imovelIds.join(',') });
  if (from) sp.set('from', from);
  if (to) sp.set('to', to);
  if (isNative()) { const tk = tokenSync(); if (tk) sp.set('token', tk); }
  const base = isNative() ? env.VITE_API_BASE : '';
  return `${base}/api/v2/agronomia/relatorio-consolidado?${sp.toString()}`;
}

// ── Manejo (telas 06-10) ─────────────────────────────────────────────────────
const manejoKey = (talhaoId: number | string, cat: string) =>
  ['agronomia', 'manejo', String(talhaoId), cat] as const;

export function useManejo(talhaoId: number, categoria: string) {
  return useQuery({
    queryKey: manejoKey(talhaoId, categoria),
    queryFn: ({ signal }) =>
      api(`/api/v2/agronomia/talhoes/${talhaoId}/manejo/${categoria}`, {
        schema: manejoAplicacaoListSchema,
        signal,
      }),
    enabled: Number.isFinite(talhaoId) && !!categoria,
    staleTime: 30_000,
    // Backend novo — enquanto a migração não roda, trate falha como "sem aplicações".
    retry: false,
  });
}

export interface NovaAplicacaoInput {
  data_aplicacao: string;
  titulo: string;
  detalhe?: string | null;
}

export function useRegistrarManejo(talhaoId: number, categoria: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NovaAplicacaoInput) =>
      api(`/api/v2/agronomia/talhoes/${talhaoId}/manejo/${categoria}`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: manejoKey(talhaoId, categoria) }),
  });
}

export interface NovaFazendaInput {
  nome: string;
  proprietario_cpf?: string | null;
  municipio?: string | null;
  uf?: string;
  tipo_posse: 'propria' | 'arrendada';
  cultura?: string | null;
  talhoes: { numero: string; nome?: string; geojson: unknown; area_ha?: number }[];
}

export function useCriarFazenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NovaFazendaInput) =>
      api('/api/v2/agronomia/fazendas', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.talhoes() });
      void qc.invalidateQueries({ queryKey: ['agronomia', 'produtor'] });
    },
  });
}

// ── Edição de fazenda ────────────────────────────────────────────────────────
export function useFazenda(imovelId: number | null) {
  return useQuery({
    queryKey: ['agronomia', 'fazenda-edit', String(imovelId ?? '')],
    queryFn: ({ signal }) =>
      api(`/api/v2/agronomia/fazendas/${imovelId}`, { schema: fazendaEditSchema, signal }),
    enabled: imovelId != null,
    staleTime: 10_000,
  });
}

// Invalida tudo que mostra nome/talhões/áreas da fazenda depois de editar.
function invalidarFazenda(qc: ReturnType<typeof useQueryClient>, imovelId: number) {
  void qc.invalidateQueries({ queryKey: ['agronomia', 'fazenda-edit', String(imovelId)] });
  void qc.invalidateQueries({ queryKey: ['agronomia', 'fazenda', String(imovelId)] });
  void qc.invalidateQueries({ queryKey: keys.talhoes() });
  void qc.invalidateQueries({ queryKey: ['agronomia', 'produtor'] });
  void qc.invalidateQueries({ queryKey: ['agronomia', 'produtores'] });
}

export interface EditarFazendaInput {
  nome?: string;
  municipio?: string | null;
  uf?: string;
  tipo_posse?: 'propria' | 'arrendada';
}

export function useEditarFazenda(imovelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EditarFazendaInput) =>
      api(`/api/v2/agronomia/fazendas/${imovelId}`, { method: 'PATCH', body }),
    onSuccess: () => invalidarFazenda(qc, imovelId),
  });
}

export function useEditarTalhao(imovelId: number) {
  const qc = useQueryClient();
  return useMutation({
    // `geojson` edita a geometria (limite OU recorte/furo) — o backend recalcula
    // area_ha por geography (o furo é descontado pelo PostGIS).
    mutationFn: ({ id, body }: {
      id: number;
      body: { nome?: string | null; area_ha?: number | null; geojson?: unknown };
    }) => api(`/api/v2/agronomia/talhoes/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidarFazenda(qc, imovelId),
  });
}

// Cria talhões novos desenhados no mapa (anexa à fazenda existente).
export function useCriarTalhoes(imovelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (talhoes: { numero: string; nome?: string | null; geojson: unknown }[]) =>
      api(`/api/v2/agronomia/fazendas/${imovelId}/talhoes`, { method: 'POST', body: { talhoes } }),
    onSuccess: () => invalidarFazenda(qc, imovelId),
  });
}

// Talhões da fazenda COM geojson (o resumo padrão descarta a geometria). Usado
// pelo editor de talhões pra carregar os polígonos editáveis.
export function useFazendaTalhoesGeom(imovelId: number) {
  return useQuery({
    queryKey: ['agronomia', 'fazenda', String(imovelId), 'talhoes-geom'],
    queryFn: ({ signal }) =>
      // Postgres manda NUMERIC/bigint como STRING → z.coerce (igual ao resumo);
      // z.number() puro quebrava o parse e os talhões sumiam.
      api(`/api/v2/imoveis/${imovelId}/talhoes`, {
        schema: z.array(z.object({
          talhao_id: z.coerce.number(),
          numero: z.string(),
          talhao_nome: z.string().nullable().optional(),
          area_ha: z.coerce.number().nullable().optional(),
          geojson: z.unknown().nullable().optional(),
        }).passthrough()),
        signal,
      }),
  });
}

export function useExcluirTalhao(imovelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/v2/agronomia/talhoes/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidarFazenda(qc, imovelId),
  });
}

// Exclui a fazenda (área) inteira — talhões/visitas/amostras cascateiam no back.
export function useExcluirFazenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imovelId: number) =>
      api(`/api/v2/agronomia/fazendas/${imovelId}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agronomia'] }),
  });
}

// Exclui o produtor e todas as áreas dele (cascade no back).
export function useExcluirProdutor() {
  const qc = useQueryClient();
  return useMutation({
    // dígitos only: CNPJ tem "/" que quebra o path param na URL.
    mutationFn: (cpf: string) =>
      api(`/api/v2/agronomia/produtores/${cpf.replace(/\D/g, '')}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agronomia'] }),
  });
}

export function useAdicionarTalhoes(imovelId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (talhoes: { numero?: string; nome?: string; geojson: unknown; area_ha?: number }[]) =>
      api(`/api/v2/agronomia/fazendas/${imovelId}/talhoes`, { method: 'POST', body: { talhoes } }),
    onSuccess: () => invalidarFazenda(qc, imovelId),
  });
}

// Cadastro de produtor = cria um cliente (PF/PJ). Aparece na lista de produtores
// depois que tiver ao menos uma fazenda vinculada (via proprietario_cpf).
export interface NovoProdutorInput {
  cpf: string;
  nome: string;
  telefone?: string | null;
  email?: string | null;
  municipio?: string | null;
  uf?: string | null;
  tipo_pessoa?: 'PF' | 'PJ';
}

export function useCriarProdutor() {
  const qc = useQueryClient();
  return useMutation({
    // Endpoint próprio da agronomia: se o CPF já é cliente, devolve sem tocar
    // (o POST /clientes faz upsert e sobrescreveria o cadastro real).
    mutationFn: (body: NovoProdutorInput) => api('/api/v2/agronomia/produtores', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agronomia', 'produtores'] }),
  });
}

// O mapa é um iframe pra um HTML Leaflet do servidor. Na web é same-origin
// (cookie autentica). No app nativo (bundle-mode) precisa de URL absoluta +
// ?token= porque o iframe não manda o header Bearer.
function mapaUrl(path: string): string {
  if (isNative()) {
    const tk = tokenSync();
    return `${env.VITE_API_BASE}${path}${tk ? `?token=${encodeURIComponent(tk)}` : ''}`;
  }
  return path;
}

/** URL servida da foto de visita (no app vai com base absoluta + ?token=). */
export function fotoUrl(fotoId: number): string {
  return mapaUrl(`/api/v2/visitas/fotos/${fotoId}`);
}

/** URL do áudio de recomendação da foto (base absoluta + ?token= no app).
 *  Serve o PRIMEIRO áudio — compat com fotos anteriores à migration 102. */
export function audioUrl(fotoId: number): string {
  return mapaUrl(`/api/v2/visitas/fotos/${fotoId}/audio`);
}

/** URL de UM áudio da foto (são vários por foto). */
export function fotoAudioUrl(audioId: number): string {
  return mapaUrl(`/api/v2/visitas/foto-audios/${audioId}`);
}

/** URL do relatório consolidado da fazenda (HTML) — abrível como link. */
export function relatorioConsolidadoUrl(imovelId: number, from?: string, to?: string): string {
  const sp = new URLSearchParams();
  if (from) sp.set('from', from);
  if (to) sp.set('to', to);
  if (isNative()) { const tk = tokenSync(); if (tk) sp.set('token', tk); }
  const qs = sp.toString();
  const base = isNative() ? env.VITE_API_BASE : '';
  return `${base}/api/v2/imoveis/${imovelId}/relatorio-consolidado${qs ? `?${qs}` : ''}`;
}

/** Relatório de UMA visita filtrado a UM talhão (histórico dentro do talhão).
 *  Mesmo layout do consolidado, só o que é daquele talhão naquela visita. */
export function relatorioTalhaoVisitaUrl(imovelId: number, visitaCampoId: number, talhaoId: number): string {
  const sp = new URLSearchParams();
  sp.set('visita', String(visitaCampoId));
  sp.set('talhao', String(talhaoId));
  if (isNative()) { const tk = tokenSync(); if (tk) sp.set('token', tk); }
  const base = isNative() ? env.VITE_API_BASE : '';
  return `${base}/api/v2/imoveis/${imovelId}/relatorio-consolidado?${sp.toString()}`;
}

/** URL do PDF do relatório consolidado. */
export function relatorioConsolidadoPdfUrl(imovelId: number, from?: string, to?: string): string {
  const sp = new URLSearchParams();
  if (from) sp.set('from', from);
  if (to) sp.set('to', to);
  if (isNative()) { const tk = tokenSync(); if (tk) sp.set('token', tk); }
  const qs = sp.toString();
  const base = isNative() ? env.VITE_API_BASE : '';
  return `${base}/api/v2/imoveis/${imovelId}/relatorio-consolidado.pdf${qs ? `?${qs}` : ''}`;
}

/** URL do mapa Leaflet do talhão (iframe). Funciona com talhão solto. */
export function talhaoMapaUrl(talhaoId: number): string {
  return mapaUrl(`/api/v2/talhoes/${talhaoId}/mapa`);
}

/** URL do mapa Leaflet com TODOS os talhões de uma fazenda. */
export function fazendaMapaUrl(imovelId: number): string {
  return mapaUrl(`/api/v2/imoveis/${imovelId}/talhoes/mapa`);
}

// ── Catálogo MIP (config, admin) ─────────────────────────────────────────
const mipKey = ['agronomia', 'mip-catalogo'] as const;

export function useMipCatalogo() {
  return useQuery({
    queryKey: mipKey,
    queryFn: ({ signal }) => api('/api/v2/mip/catalogo', { schema: mipCatalogoSchema, signal }),
    staleTime: 60_000,
  });
}

export interface MipItemInput {
  categoria: string;
  nome: string;
  nome_cientifico?: string | null;
  unidade?: string;
  fases?: string[];
  limiar_atencao?: number | null;
  limiar_acao?: number | null;
  ordem?: number;
}

export function useCreateMipItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MipItemInput) => api('/api/v2/mip/itens', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mipKey }),
  });
}

export function useUpdateMipItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<MipItemInput> }) =>
      api(`/api/v2/mip/itens/${id}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mipKey }),
  });
}

export function useDeleteMipItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/v2/mip/itens/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mipKey }),
  });
}

export interface MipCompostoInput {
  nome?: string;
  fase?: string | null;
  limiar_acao?: number | null;
  unidade?: string | null;
}

export function useUpdateMipComposto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: MipCompostoInput }) =>
      api(`/api/v2/mip/compostos/${id}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mipKey }),
  });
}

// ── Catálogo de insumos (099) ────────────────────────────────────────────────
export function useInsumos() {
  return useQuery({
    queryKey: ['agronomia', 'insumos'],
    queryFn: ({ signal }) => api('/api/v2/agronomia/insumos', { schema: insumoListSchema, signal }),
    staleTime: 60_000,
  });
}

// Payload do cadastro de insumo (tela 02 · Novo insumo).
export interface NovoInsumoPayload {
  nome_comercial: string;
  categoria: string;
  fabricante?: string | null;
  formulacao?: string | null;
  unidade?: string | null;
  modo_acao?: string | null;
  culturas: string[];
  reentrada_horas?: number | null;
  classe_tox?: string | null;
  classe_ambiental?: string | null;
  numero_registro?: string | null;
  fonte_bula_url?: string | null;
  ativos: {
    nome: string;
    concentracao: number | null;
    concentracao_unidade: string | null;
    grupo_quimico: string | null;
    ph: number | null;
    kow: number | null;
  }[];
  culturas_bula: {
    cultura: string;
    dose_min: number | null;
    dose_max: number | null;
    dose_unidade: string | null;
    carencia_dias: number | null;
    max_aplicacoes: number | null;
    intervalo_dias: number | null;
  }[];
}

export function useCriarInsumo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NovoInsumoPayload) =>
      api('/api/v2/agronomia/insumos', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agronomia', 'insumos'] });
    },
  });
}

export function useEditarInsumo(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NovoInsumoPayload) =>
      api(`/api/v2/agronomia/insumos/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agronomia', 'insumos'] });
    },
  });
}
